import * as React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@wayste/shadcn/components/tooltip';
import { cn } from '@wayste/shadcn/lib/utils';
import type { LucideIcon } from 'lucide-react';
import { Eye, Info, TrendingDown, TrendingUp } from 'lucide-react';

export interface StatCardProps extends React.ComponentProps<'div'> {
    title: string;
    value: React.ReactNode;
    description?: string;
    icon?: LucideIcon;
    iconClassName?: string;
    infoTooltip?: string;
    trend?: {
        value: number;
        label?: string;
    };
    action?: {
        label: string;
        onClick?: () => void;
        href?: string;
    };
    variant?: 'default' | 'accent';
    /** Controls the size of the main value display. Maps to Figma typography tokens:
     * - 'h2': Desktop/heading/h2 (18px semibold)
     * - 'h3': Desktop/heading/h3 (16px semibold)
     * - 'h4': Desktop/heading/h4 (14px semibold)
     */
    valueSize?: 'h2' | 'h3' | 'h4';
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
    (
        {
            className,
            title,
            value,
            description,
            icon: Icon,
            iconClassName,
            infoTooltip,
            trend,
            action,
            variant = 'default',
            valueSize = 'h2',
            ...props
        },
        ref,
    ) => {
        const isPositiveTrend = trend && trend.value >= 0;
        const trendColorClass = isPositiveTrend ? 'text-success-on-surface-text' : 'text-destructive-on-surface-text';
        const trendBgClass = isPositiveTrend ? 'bg-success-surface-default' : 'bg-destructive-surface-default';

        return (
            <div
                ref={ref}
                className={cn(
                    'bg-surface-tertiary border-border-default flex flex-col justify-between gap-2 rounded-lg border p-4',
                    variant === 'accent' && 'bg-info-surface-default',
                    className,
                )}
                {...props}
            >
                {/* Header: Icon + Title + Info/Action icons */}
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                        {Icon && <Icon className={cn('size-4', iconClassName)} />}
                        <span className="text-text-secondary text-[12px] font-medium leading-4 tracking-[0.24px]">{title}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {infoTooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info className="text-text-secondary size-4 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="max-w-xs text-xs">{infoTooltip}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        {action && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {action.href ? (
                                            <a
                                                href={action.href}
                                                onClick={action.onClick}
                                                aria-label={action.label}
                                                className="text-text-secondary hover:text-text-primary"
                                            >
                                                <Eye className="size-4" aria-hidden="true" />
                                            </a>
                                        ) : (
                                            <button
                                                onClick={action.onClick}
                                                aria-label={action.label}
                                                className="text-text-secondary hover:text-text-primary"
                                            >
                                                <Eye className="size-4" aria-hidden="true" />
                                            </button>
                                        )}
                                    </TooltipTrigger>
                                    <TooltipContent className="w-auto">
                                        <p className="text-xs">{action.label}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>

                {/* Value + Trend pill */}
                <div className="flex items-center gap-2">
                    <div
                        className={cn(
                            'text-text-primary',
                            valueSize === 'h2' && 'text-[18px] font-semibold leading-7 tracking-[-0.18px]',
                            valueSize === 'h3' && 'text-base font-semibold leading-6',
                            valueSize === 'h4' && 'text-sm font-semibold leading-5',
                        )}
                    >
                        {value}
                    </div>
                    {trend !== undefined && (
                        <div
                            className={cn(
                                'flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium',
                                trendBgClass,
                                trendColorClass,
                            )}
                        >
                            {isPositiveTrend ? (
                                <TrendingUp className="size-3" aria-hidden="true" />
                            ) : (
                                <TrendingDown className="size-3" aria-hidden="true" />
                            )}
                            <span>
                                {isPositiveTrend ? '+' : ''}
                                {trend.value}%
                            </span>
                        </div>
                    )}
                </div>

                {/* Description or Trend Label */}
                {(description || trend?.label) && (
                    <div className="text-text-secondary text-[11px] font-normal leading-4">
                        {trend?.label && <span className={trendColorClass}>{trend.label}</span>}
                        {description}
                    </div>
                )}
            </div>
        );
    },
);

StatCard.displayName = 'StatCard';

export { StatCard };
